import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  CatalogValidationError,
  useCatalogCategories,
  useCreateCatalogCategory,
  useSetCatalogCategoryRetired,
  useUpdateCatalogCategory,
  type CatalogCategory,
} from '@/routes/pricebook/usePricebook';

/**
 * The account's price-book categories (M2-02), at `/pricebook/categories`.
 *
 * `LeadSourceCatalog` with the nouns changed — the same interaction over the
 * same entity shape, kept as its own component for the reason that screen
 * gives. Gated on `pricebook.manage`, matching the API, and reached from the
 * price book rather than the nav.
 *
 * One difference from the screens it copies: <b>restore can be refused.</b>
 * If an active category has taken a retired one's label since, the API says so
 * rather than putting the same word in the filter twice, and the row shows the
 * server's sentence.
 */
export function PricebookCategories() {
  const { t } = useTranslation();
  const categories = useCatalogCategories(true);
  const create = useCreateCatalogCategory();

  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');

  function addCategory() {
    const label = newLabel.trim();
    if (!label) {
      return;
    }
    setError('');
    create.mutate(label, {
      onSuccess: (created) => {
        setNewLabel('');
        setAnnouncement(t('pricebookCategories.added', { label: created.label }));
      },
      onError: (err) =>
        setError(
          err instanceof CatalogValidationError ? err.message : t('pricebookCategories.addFailed'),
        ),
    });
  }

  return (
    <section className="space-y-4">
      <div>
        <Link to="/pricebook" className="text-sm underline underline-offset-2">
          {t('pricebookCategories.backToPricebook')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t('pricebookCategories.heading')}
        </h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('pricebookCategories.description')}
        </p>
      </div>

      {categories.isPending ? (
        <LoadingState label={t('pricebookCategories.loading')} />
      ) : categories.isError ? (
        <ErrorState
          title={t('pricebookCategories.loadErrorTitle')}
          onRetry={() => void categories.refetch()}
        />
      ) : (
        <div className="space-y-3 rounded-lg border p-4">
          {categories.data.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {t('pricebookCategories.empty')}
            </p>
          ) : (
            <ul className="space-y-1" data-testid="pricebook-category-list">
              {categories.data.map((category) => (
                <CategoryRow key={category.id} category={category} onAnnounce={setAnnouncement} />
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2 border-t border-[var(--color-border)] pt-3">
            <label className="block flex-1 text-sm">
              <span className="mb-1 block font-medium">{t('pricebookCategories.newLabel')}</span>
              <input
                className={FIELD_CLASS}
                value={newLabel}
                maxLength={100}
                onChange={(event) => setNewLabel(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    addCategory();
                  }
                }}
              />
            </label>
            <Button
              type="button"
              size="sm"
              disabled={!newLabel.trim() || create.isPending}
              onClick={addCategory}
            >
              {t('pricebookCategories.add')}
            </Button>
          </div>

          {error ? (
            <p role="alert" className="text-sm text-[var(--color-destructive)]">
              {error}
            </p>
          ) : null}
        </div>
      )}

      <p
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="pricebook-categories-announcement"
      >
        {announcement}
      </p>
    </section>
  );
}

/** One category — rename inline, retire or restore. Never deleted: items reference it. */
function CategoryRow({
  category,
  onAnnounce,
}: {
  category: CatalogCategory;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateCatalogCategory();
  const setRetired = useSetCatalogCategoryRetired();
  const [label, setLabel] = useState(category.label);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus follows the person's own "Rename" click — LeadSourceCatalog's shape,
  // without the declarative autoFocus jsx-a11y rejects.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function cancel() {
    setEditing(false);
    setError('');
    setLabel(category.label);
  }

  function save() {
    const trimmed = label.trim();
    if (!trimmed || trimmed === category.label) {
      cancel();
      return;
    }
    setError('');
    update.mutate(
      { id: category.id, label: trimmed, sortOrder: Number(category.sortOrder) },
      {
        onSuccess: (saved) => {
          setEditing(false);
          onAnnounce(t('pricebookCategories.renamed', { label: saved.label }));
        },
        onError: (err) =>
          setError(
            err instanceof CatalogValidationError
              ? err.message
              : t('pricebookCategories.renameFailed'),
          ),
      },
    );
  }

  function toggleRetired() {
    setError('');
    setRetired.mutate(
      { id: category.id, retired: category.isActive },
      {
        onSuccess: () =>
          onAnnounce(
            category.isActive
              ? t('pricebookCategories.retiredAnnouncement', { label: category.label })
              : t('pricebookCategories.restoredAnnouncement', { label: category.label }),
          ),
        onError: (err) =>
          setError(
            err instanceof CatalogValidationError
              ? err.message
              : t('pricebookCategories.retireFailed'),
          ),
      },
    );
  }

  return (
    <li
      data-testid="pricebook-category-row"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
    >
      {editing ? (
        <span className="flex flex-1 items-center gap-2">
          <label htmlFor={`catalog-category-${category.id}`} className="sr-only">
            {t('pricebookCategories.renameLabelFor', { label: category.label })}
          </label>
          <input
            id={`catalog-category-${category.id}`}
            ref={inputRef}
            className={FIELD_CLASS}
            value={label}
            maxLength={100}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                save();
              }
              if (event.key === 'Escape') {
                cancel();
              }
            }}
          />
          <button type="button" className="text-xs underline underline-offset-2" onClick={save}>
            {t('common.save')}
          </button>
        </span>
      ) : (
        <span className="flex flex-1 flex-wrap items-center gap-2">
          <span
            className={category.isActive ? '' : 'text-[var(--color-muted-foreground)] line-through'}
          >
            {category.label}
          </span>
          {!category.isActive ? (
            <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
              {t('pricebookCategories.retiredChip')}
            </span>
          ) : null}
        </span>
      )}

      <span className="flex items-center gap-3">
        {!editing ? (
          <button
            type="button"
            className="text-xs underline underline-offset-2"
            aria-label={t('pricebookCategories.renameLabelFor', { label: category.label })}
            onClick={() => setEditing(true)}
          >
            {t('pricebookCategories.rename')}
          </button>
        ) : null}
        <button
          type="button"
          className="text-xs underline underline-offset-2"
          aria-label={
            category.isActive
              ? t('pricebookCategories.retireLabelFor', { label: category.label })
              : t('pricebookCategories.restoreLabelFor', { label: category.label })
          }
          disabled={setRetired.isPending}
          onClick={toggleRetired}
        >
          {category.isActive ? t('pricebookCategories.retire') : t('pricebookCategories.restore')}
        </button>
      </span>

      {error ? (
        <p role="alert" className="w-full text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </li>
  );
}
